import { useState, useMemo, useCallback } from "react";
import { X, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY_INFO } from "@/lib/companyInfo";
import { formatCurrency, formatCurrencyWhole, COST_TYPES, deriveCostType, calcLineItemTotals, calcScopeCost, summarizeProposalDisplay } from "./proposalUtils";
import { format } from "date-fns";
import { toast } from "sonner";
import { generateProposalPDF } from "./generateProposalPDF";
import { downloadPdfWithSaveAs } from "@/lib/downloadPdfWithSaveAs";
import { TaxHiddenBadge } from "@/components/common/TaxHiddenBadge";
import { TaxOverrideBadge } from "@/components/common/TaxOverrideBadge";

export function ProposalPreview({ form, proposalId, clients, sites, totals, onClose }) {
  const client = clients.find(c => c.id === form.client_id);
  const site = sites?.find(s => s.id === form.site_id);
  const clientName = client?.name || "Customer";
  const proposalDate = form.proposal_date ? format(new Date(form.proposal_date + "T00:00:00"), "MMMM d, yyyy") : format(new Date(), "MMMM d, yyyy");
  const quals = form.qualifications.filter(q => q.trim());
  // Site always has a visible name — defaults to "Main" if the proposal
  // doesn't reference a linked site (matches the server-side default so
  // PDF and email previews stay in lock-step).
  const siteName = site?.name || "Main";
  // Address falls back through: explicit project_address → linked site
  // address → client billing address. Guarantees no blank "—" on the
  // customer-facing preview when ANY address data exists.
  const buildAddress = (obj) => obj ? [obj.address, [obj.city, obj.state, obj.zip_code].filter(Boolean).join(", ")].filter(Boolean).join(", ") : "";
  const siteAddr = form.project_address || buildAddress(site) || buildAddress(client);
  // T&M proposals show a Time & Materials statement instead of the
  // priced scope tables + totals (stripped-down customer view).
  const isTM = form.project_type === "tm";
  // iter244 — aggregate add-ons for the totals summary; scope tables show
  // line items only so flat-rate prices print exactly as entered.
  const disp = isTM ? null : summarizeProposalDisplay(form.scopes || [], form.cc_fee_pct == null ? 0 : form.cc_fee_pct);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-8" onClick={onClose}>
      <div
        className="bg-white w-[816px] min-h-[1056px] shadow-2xl rounded-lg"
        onClick={e => e.stopPropagation()}
        data-testid="proposal-preview"
      >
        {/* Close bar */}
        <div className="sticky top-0 z-10 bg-slate-800 text-white px-6 py-2 rounded-t-lg flex justify-between items-center">
          <span className="text-sm font-medium">Proposal Preview</span>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" className="h-7 bg-white text-slate-800 hover:bg-slate-100"
              onClick={async () => {
                if (proposalId) {
                  const baseUrl = process.env.REACT_APP_BACKEND_URL;
                  const url = `${baseUrl}/api/proposals/${proposalId}/pdf?download=1`;
                  const safeName = (form.name || "Proposal").replace(/[^a-z0-9]+/gi, "_");
                  try {
                    const result = await downloadPdfWithSaveAs(url, `ABS_Proposal_${safeName}.pdf`);
                    if (result === "save-as") toast.success("PDF saved");
                    else if (result === "cancelled") {}
                    else if (result === "anchor") toast.success("PDF downloading...");
                    else toast.success("Opening PDF download...");
                  } catch (err) { toast.error("Download failed: " + (err?.message || "unknown error")); }
                  return;
                }
                try { generateProposalPDF(form, clients, sites, totals); }
                catch (err) { toast.error("Failed to generate PDF: " + (err?.message || "unknown error")); }
              }}
              data-testid="btn-preview-download-pdf">
              <FileDown className="w-3.5 h-3.5 mr-1" /> Download PDF
            </Button>
            <Button variant="ghost" size="sm" className="text-white hover:bg-slate-700 h-7" onClick={onClose} data-testid="btn-close-preview">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Page content */}
        <div className="px-12 py-10" style={{ fontFamily: "'Segoe UI', sans-serif" }}>
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <img src="/abs-logo.png" alt="ABS" className="abs-logo h-16 object-contain" data-testid="preview-abs-logo" />
            <div className="text-right text-xs text-slate-500 leading-relaxed">
              <div className="font-semibold text-slate-700 text-sm">{COMPANY_INFO.name}</div>
              <div>{COMPANY_INFO.fullAddress}</div>
              <div>Phone: {COMPANY_INFO.phone}</div>
              <div>Email: {COMPANY_INFO.email}</div>
            </div>
          </div>

          {/* Meta — BILL TO / PROJECT / SITE / ADDRESS */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm mb-6">
            <div className="min-w-0">
              <div className="text-slate-400 text-[10px] uppercase tracking-wide font-semibold">Bill To</div>
              <div className="font-medium text-slate-800 break-words">{clientName || "—"}</div>
            </div>
            <div className="text-right min-w-0">
              <div className="text-slate-400 text-[10px] uppercase tracking-wide font-semibold">Project</div>
              <div className="font-medium text-slate-800 break-words">{form.name || "—"}</div>
            </div>
            <div className="min-w-0">
              <div className="text-slate-400 text-[10px] uppercase tracking-wide font-semibold">Site</div>
              <div className="font-medium text-slate-700 break-words">{siteName}</div>
            </div>
            <div className="text-right min-w-0">
              <div className="text-slate-400 text-[10px] uppercase tracking-wide font-semibold">Address</div>
              <div className="text-slate-600 break-words">{siteAddr || "—"}</div>
            </div>
            <div className="min-w-0">
              <div className="text-slate-400 text-[10px] uppercase tracking-wide font-semibold">Proposal Date</div>
              <div className="font-medium text-slate-700">{proposalDate}</div>
            </div>
            <div className="text-right min-w-0">
              <div className="text-slate-400 text-[10px] uppercase tracking-wide font-semibold">Account Manager</div>
              <div className="font-medium text-slate-700">{form.prepared_by || "—"}{form.prepared_by_email ? ` — ${form.prepared_by_email}` : ""}</div>
            </div>
          </div>

          {/* Qualifications */}
          {quals.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700 mb-2">Qualifications</h3>
              <ul className="list-disc pl-5 text-sm text-slate-600 space-y-0.5">
                {quals.map((q) => <li key={`qual-${q}`}>{q}</li>)}
              </ul>
            </div>
          )}

          {/* Terms */}
          {Array.isArray(form.terms) && form.terms.filter(t => t.trim()).length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700 mb-2">Terms</h3>
              <ul className="list-disc pl-5 text-sm text-slate-600 space-y-0.5">
                {form.terms.filter(t => t.trim()).map((t) => <li key={`term-${t}`}>{t}</li>)}
              </ul>
            </div>
          )}

          {/* T&M — statement block replaces the priced scope tables */}
          {isTM && (
            <div className="mb-6" data-testid="proposal-preview-tm-block">
              <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700 mb-2">Time &amp; Materials</h3>
              <p className="text-sm text-slate-600">
                This project will be performed and billed on a Time &amp; Materials basis. Labor, materials,
                freight, tax, overtime, and applicable markups are billed as incurred per the qualifications above.
              </p>
            </div>
          )}

          {/* Scopes — respects form.show_breakdown to show Material + Labor
              columns (one row per item) vs single Total column. CC fee is
              silently distributed into each scope's material line items.
              iter244 — Drive Time / Travel / Equipment / Disposal are NOT
              table rows; they render as aggregate lines in the totals
              summary below so flat-rate line prices stay intact. */}
          {!isTM && (() => {
            const ccScopes = disp.ccScopes;
            return ccScopes.map((scope, idx) => {
            const breakout = !!form.show_breakdown;
            const lineRows = (scope._lines || []).map(li => ({
              id: li.id,
              partNumber: li.part_number || "",
              name: li.name || li.description || "—",
              // Keep the raw description so the preview can render it on a
              // separate sub-line under the name when both are present and
              // wrap multi-line content cleanly. PDF mirrors this layout.
              description: (li.name && li.description && li.description !== li.name) ? li.description : "",
              qty: parseInt(li.qty) || 1,
              material: li._displayMaterial || 0,
              labor: li._displayLabor || 0,
              freight: li._displayFreight || 0,
              total: li._displayTotal || 0,
            }));

            return (
              <div key={scope.id} className="mb-5">
                <h3 className="text-sm font-bold text-slate-800 mb-1 uppercase tracking-wide">{scope.name || `Scope ${idx + 1}`}</h3>
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-700 text-white">
                      <th className="text-left px-2 py-1.5 font-semibold">Part #</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Name</th>
                      <th className="text-center px-2 py-1.5 font-semibold">Qty</th>
                      {breakout && <th className="text-right px-2 py-1.5 font-semibold" data-testid={`proposal-preview-col-material-${idx}`}>Material</th>}
                      {breakout && <th className="text-right px-2 py-1.5 font-semibold" data-testid={`proposal-preview-col-freight-${idx}`}>Freight</th>}
                      {breakout && <th className="text-right px-2 py-1.5 font-semibold" data-testid={`proposal-preview-col-labor-${idx}`}>Labor</th>}
                      <th className="text-right px-2 py-1.5 font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineRows.map((r, itemIdx) => (
                      <tr key={r.id} className={itemIdx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                        <td className="px-2 py-1.5 text-slate-500 font-mono align-top">{r.partNumber || "—"}</td>
                        <td className="px-2 py-1.5 font-medium text-slate-700 align-top">
                          <div>{r.name}</div>
                          {r.description && (
                            <div className="text-[11px] text-slate-500 font-normal whitespace-pre-wrap mt-0.5 leading-snug">
                              {r.description}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-center align-top">{r.qty}</td>
                        {breakout && <td className="px-2 py-1.5 text-right tabular-nums align-top" data-testid={`proposal-preview-material-${idx}-${itemIdx}`}>{formatCurrency(r.material)}</td>}
                        {breakout && <td className="px-2 py-1.5 text-right tabular-nums align-top" data-testid={`proposal-preview-freight-${idx}-${itemIdx}`}>{formatCurrency(r.freight)}</td>}
                        {breakout && <td className="px-2 py-1.5 text-right tabular-nums align-top" data-testid={`proposal-preview-labor-${idx}-${itemIdx}`}>{formatCurrency(r.labor)}</td>}
                        <td className="px-2 py-1.5 text-right tabular-nums font-medium align-top">{formatCurrency(r.total)}</td>
                      </tr>
                    ))}
                    {/* iter244 — Travel/Equipment/Disposal/Drive Time no longer
                        render inside the scope table; aggregated in the totals
                        summary below. */}
                  </tbody>
                </table>
              </div>
            );
          });
          })()}

          {/* Grand Totals — freight is silently lumped into material (no
              standalone line). Subtotal shows the full pre-tax billed amount. */}
          {!isTM && (
          <div className="flex justify-end mt-4">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between text-slate-500">
                <span>Subtotal</span>
                <span className="tabular-nums font-medium" data-testid="proposal-preview-items-subtotal">{formatCurrency(disp?.itemsSubtotal || 0)}</span>
              </div>
              {(disp?.driveTime || 0) > 0 && (
                <div className="flex justify-between text-slate-500" data-testid="proposal-preview-drivetime">
                  <span>Drive Time</span>
                  <span className="tabular-nums font-medium">{formatCurrency(disp.driveTime)}</span>
                </div>
              )}
              {(disp?.equipment || 0) > 0 && (
                <div className="flex justify-between text-slate-500" data-testid="proposal-preview-equipment">
                  <span>Equipment</span>
                  <span className="tabular-nums font-medium">{formatCurrency(disp.equipment)}</span>
                </div>
              )}
              {(disp?.travel || 0) > 0 && (
                <div className="flex justify-between text-slate-500" data-testid="proposal-preview-travel">
                  <span>Travel</span>
                  <span className="tabular-nums font-medium">{formatCurrency(disp.travel)}</span>
                </div>
              )}
              {(disp?.disposal || 0) > 0 && (
                <div className="flex justify-between text-slate-500" data-testid="proposal-preview-disposal">
                  <span>Disposal</span>
                  <span className="tabular-nums font-medium">{formatCurrency(disp.disposal)}</span>
                </div>
              )}
              {(totals.discountAmount || 0) > 0 && (
                <div className="flex justify-between text-emerald-700" data-testid="proposal-preview-discount">
                  <span>
                    {(totals.discountType || form.discount_type) === "pct"
                      ? `Discount (${parseFloat(form.discount_value) || 0}%)`
                      : "Discount"}
                  </span>
                  <span className="tabular-nums font-medium">-{formatCurrency(totals.discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between text-slate-500">
                <span>Tax ({parseFloat(form.tax_rate) || 0}%)</span>
                <span className="tabular-nums font-medium">{formatCurrency(totals.taxAmount)}</span>
              </div>
              {form.hide_tax && (
                <div className="flex justify-end print:hidden" data-testid="proposal-preview-tax-hidden">
                  <TaxHiddenBadge taxAmount={totals.taxAmount} taxRate={form.tax_rate} testId="proposal-preview-tax-hidden-badge" />
                </div>
              )}
              {form.tax_override && (
                <div className="flex justify-end print:hidden" data-testid="proposal-preview-tax-override">
                  <TaxOverrideBadge override={form.tax_override} stale={totals.taxOverrideStale} canManage={false} testId="proposal-preview-tax-badge" />
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-slate-300 text-lg font-bold text-slate-900">
                <span>Total</span>
                <span className="tabular-nums">{formatCurrency(totals.total)}</span>
              </div>
            </div>
          </div>
          )}

          {/* Acceptance */}
          <div className="mt-12 pt-6 text-xs text-slate-400 space-y-4">
            <div className="flex gap-8">
              <div className="flex-1">
                <div className="mb-1">Accepted By:</div>
                {form.signature_data ? (
                  <div className="border-b border-slate-300 pb-1" data-testid="proposal-preview-signature">
                    <img src={form.signature_data} alt="Customer signature" className="h-14 object-contain" />
                    {form.signed_by && (
                      <div className="text-[10px] text-slate-600 mt-0.5">
                        {form.signed_by}
                        {form.signed_at ? ` — ${format(new Date(form.signed_at), "MMM d, yyyy")}` : ""}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="border-b border-slate-300 h-8"></div>
                )}
              </div>
              <div className="w-40">
                <div className="mb-1">Date Accepted:</div>
                {form.signed_at ? (
                  <div className="border-b border-slate-300 pb-1 text-slate-700">
                    {format(new Date(form.signed_at), "MMM d, yyyy")}
                  </div>
                ) : (
                  <div className="border-b border-slate-300 h-8"></div>
                )}
              </div>
            </div>
            {/* iter305 — printed name, title and PO # complete the block. */}
            <div className="flex gap-8">
              <div className="flex-1">
                <div className="mb-1">Printed Name:</div>
                <div className="border-b border-slate-300 h-7 text-slate-700 text-[11px] pt-1">
                  {form.signed_by || ""}
                </div>
              </div>
              <div className="w-40">
                <div className="mb-1">Title:</div>
                <div className="border-b border-slate-300 h-7"></div>
              </div>
            </div>
            <div className="flex gap-8">
              <div className="flex-1">
                <div className="mb-1">PO # (if applicable):</div>
                <div className="border-b border-slate-300 h-7 text-slate-700 text-[11px] pt-1"
                     data-testid="proposal-preview-po">
                  {(form.customer_po_number || "").trim()}
                </div>
              </div>
              <div className="w-40" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============== PDF GENERATION ==============

